// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  BizMetricAttributedData,
  getBusinessVerticals,
} from './utils';
import { Divider, Grid, Header, Icon, Popup } from 'semantic-grab-ui';
import { Select } from 'grab-ui';
import 'chartjs-plugin-datalabels';
import { countriesCurrency } from 'data/trident-campaigns';
import { BusinessMetricChart } from 'omega-rtc/views/trident-campaigns/metrics/BusinessMetricChart';
import {
  colorPaletteList,
  DEFAULT_VERTICAL,
  GMV_KEY,
  headerTextInfoMapping,
  labelMetricTypeMapping,
  RAW_KEY,
} from 'omega-rtc/constants/metrics';
import BusinessMetricsInsight from 'omega-rtc/views/trident-campaigns/metrics/BusinessMetricsInsight';

type Props = {
  metrics: Array<BizMetricAttributedData>,
  metricType: string,
  countryID: number,
  hasExpNode: boolean,
  node: any, // the root node
};

const attributedDropDownOptions = (keys: Array<string>, metricType: string) => {
  return _.map(keys, key => ({
    key,
    label: `${metricType === RAW_KEY ? 'Accumulated' : 'Attributed'} ${key === GMV_KEY ? 'GMV' : _.capitalize(key)} per user`,
  }));
};

// BusinessMetricSectionView ...
export default class BusinessMetricSectionView extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedVertical: DEFAULT_VERTICAL,
      attributedDataKey: GMV_KEY,
    };
  }

  handleDataKeyChange = (field: string, value: string) => {
    this.setState({
      [field]: value,
    });
  }

  // getMetricsTimeWindow returns the node's startTime, endTime if the time difference is smaller than 30 days
  // otherwise it returns startTime, (startTime + 30 days)
  getMetricsTimeWindow = (node: any) => {
    const windowStart = node.startTime;
    let windowEnd = node.endTime;
    if (windowEnd.diff(windowStart, 'days') > 30) {
      windowEnd = moment(windowStart).add(30, 'days');
    }
    return {
      windowStart, windowEnd,
    };
  }

  render() {
    const { metricType, metrics, countryID, node } = this.props;
    const { selectedVertical, attributedDataKey } = this.state;
    let attributeKeys = [];
    const distinctUserCountArray = [];
    const valuesVerticalAttributeKey = [];
    const valuesAvgVerticalAttributeKey = [];
    const chartLabels = [];
    _.forEach(metrics, (val) => {
      attributeKeys = _.union(_.keys(_.get(val, 'data', {})), attributeKeys);
      distinctUserCountArray.push(_.get(val, 'distinctUserCount', 0));
      valuesVerticalAttributeKey.push(_.get(val, `data.${attributedDataKey}.${selectedVertical}.total`, 0));
      valuesAvgVerticalAttributeKey.push(_.get(val, `data.${attributedDataKey}.${selectedVertical}.average`, 0));
      chartLabels.push(val.name);
    });
    const currencyVal = countriesCurrency[countryID];
    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: '',
          data: valuesAvgVerticalAttributeKey,
          borderWidth: 0,
          barThickness: 15,
          backgroundColor: colorPaletteList.slice(0, valuesAvgVerticalAttributeKey.length + 1),
        },
      ],
    };

    // get metrics time window
    const { windowStart, windowEnd } = this.getMetricsTimeWindow(node);

    // we have array of width to help calculate chart row width
    // num of labels > 8, its 12
    // for less than 8, we have value (currently step values)
    const arrCalc = [7, 7, 8, 8, 9, 9, 10, 10];
    const calcWidth = chartLabels.length > arrCalc.length ?
      arrCalc[arrCalc.length - 1] + 2 :
      arrCalc[chartLabels.length - 1];
    return (
      <div>
        <Grid style={{ margin: 0 }}>
          <Grid.Row style={{ alignItems: 'baseline' }}>
            <Header
              as="h4"
              style={{ marginTop: 8, marginBottom: 24, marginRight: 5, fontWeight: 'normal', borderBottom: '5px solid #0931a7' }}
            >
              {labelMetricTypeMapping[metricType].sectionTitle}
            </Header>
            <Popup
              hoverable
              trigger={<Icon name="question circle" />}
            >
              {labelMetricTypeMapping[metricType].sectionInfo}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={headerTextInfoMapping.hereLink}
              >
                {' HERE '}
              </a>
            </Popup>
          </Grid.Row>
          <Grid.Row>
            <Header
              as="h5"
              style={{ marginTop: 8, fontWeight: 'normal', marginRight: '10px' }}
            >
              {headerTextInfoMapping.vertical}
            </Header>
            <Select
              defaultValue={selectedVertical}
              onChange={val => this.handleDataKeyChange('selectedVertical', val)}
            >
              {_.map(getBusinessVerticals(metrics), (dropDownOption) => (<Select.Option
                value={dropDownOption}
              >{_.capitalize(dropDownOption)}</Select.Option>))}
            </Select>
            <Divider style={{ marginRight: '20px', marginLeft: '20px' }} type="vertical" />
            <Header
              as="h5"
              style={{ marginTop: 8, fontWeight: 'normal', marginRight: '10px' }}
            >
              {headerTextInfoMapping.metrics}
            </Header>
            <Select
              defaultValue={attributedDataKey}
              onChange={val => this.handleDataKeyChange('attributedDataKey', val)}
            >
              {_.map(attributedDropDownOptions(attributeKeys, metricType), (dropDownOption) => (<Select.Option
                value={dropDownOption.key}
              >{dropDownOption.label}</Select.Option>))}
            </Select>
          </Grid.Row>
          <Grid.Row>
            Accumulation Time Window: &nbsp;&nbsp;
            {/* {getTotalMetricLabel(selectedVertical, attributedDataKey, metricType)}&nbsp; */}
            <b>
              {windowStart.format('MMM Do YYYY')} ~ {windowEnd.format('MMM Do YYYY')}
              {/* {attributedDataKey === GMV_KEY ? currencyVal : ''}&nbsp; */}
              {/* {numberWithCommas(_.round(_.reduce(valuesVerticalAttributeKey, (sum, n) => sum + n, 0), 3))} */}
            </b>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={calcWidth}>
              <BusinessMetricChart
                chartData={chartData}
                valuesVerticalAttributeKey={valuesVerticalAttributeKey}
                attributedDataKey={attributedDataKey}
                selectedVertical={selectedVertical}
                distinctUserCountArray={distinctUserCountArray}
                currencyVal={currencyVal}
                metricType={metricType}
                chartLabels={chartLabels}
                valuesAvgVerticalAttributeKey={valuesAvgVerticalAttributeKey}
              />
            </Grid.Column>
          </Grid.Row>
          <BusinessMetricsInsight
            metrics={metrics}
            attributedDataKey={attributedDataKey}
            selectedVertical={selectedVertical}
            metricType={metricType}
          />
        </Grid>
      </div>
    );
  }
}
